import { Inject, Injectable, InjectionToken } from '@angular/core';
import {
  IDynamicItemMergeOptions,
  isIDynamicItemMergeOptions,
  PartialIDynamicItem,
} from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';
import { forkJoin, isObservable, map, mergeMap, Observable, of } from 'rxjs';

export type FetchResult = PartialIDynamicItem[] | null;

export interface IFetchConfigService {
  canFetchFrom(source: string): boolean;
  fetch(key: string): Observable<FetchResult> | FetchResult;
}

export const FETCH_CONFIG_SERVICES = new InjectionToken<IFetchConfigService[]>(
  'FetchConfigServices',
);

@Injectable({
  providedIn: 'root',
})
export class FetchConfigService {
  constructor(
    @Inject(FETCH_CONFIG_SERVICES)
    private readonly fetchConfigServices: IFetchConfigService[],
  ) {}

  public fetch(source: string, key: string): Observable<PartialIDynamicItem[]> {
    return this.fetchInternal(source, key).pipe(
      mergeMap((fetchResult) => {
        const toFetch: Observable<
          PartialIDynamicItem | PartialIDynamicItem[]
        >[] = [];
        for (const entry of fetchResult) {
          if (isIDynamicItemMergeOptions(entry)) {
            toFetch.push(this.fetch(entry.source, entry.key));
          } else {
            toFetch.push(of(entry));
          }
        }

        let returnValue: Observable<
          (PartialIDynamicItem | PartialIDynamicItem[])[]
        >;
        if (toFetch.length > 0) {
          returnValue = forkJoin(toFetch);
        } else {
          returnValue = of([]);
        }

        return returnValue;
      }),
      map((partialDynamicItems) => {
        const items: PartialIDynamicItem[] = [];

        for (const itemOrArray of partialDynamicItems) {
          if (Array.isArray(itemOrArray)) {
            items.push(...itemOrArray);
          } else {
            items.push(itemOrArray);
          }
        }

        return items;
      }),
    );
  }

  private fetchInternal(
    source: string,
    key: string,
  ): Observable<(PartialIDynamicItem | IDynamicItemMergeOptions)[]> {
    for (const service of this.fetchConfigServices) {
      if (service.canFetchFrom(source)) {
        return this.fetchConfigFromSource(service, key, source);
      }
    }

    throw new Error(
      `No service has been registered that can fetch key '${key}' from source '${source}'.`,
    );
  }

  private fetchConfigFromSource(
    service: IFetchConfigService,
    key: string,
    source: string,
  ): Observable<(PartialIDynamicItem | IDynamicItemMergeOptions)[]> {
    let config = service.fetch(key);

    if (!isObservable(config)) {
      config = of(config);
    }

    return config.pipe(
      map((fetchResult) => {
        if (fetchResult === undefined || fetchResult === null) {
          throw new Error(
            `Fetching config with key '${key}' from source '${source}' returned null.`,
          );
        }

        return fetchResult as PartialIDynamicItem[];
      }),
    );
  }
}
